import { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useSettings } from '@/SettingsContext';
import { useLanguage } from '@/LanguageContext';

// ====================================================================
//
//   ███╗   ███╗ █████╗ ███████╗████████╗███████╗██████╗     ██████╗  █████╗ ███╗   ██╗███████╗██╗
//   ████╗ ████║██╔══██╗██╔════╝╚══██╔══╝██╔════╝██╔══██╗    ██╔══██╗██╔══██╗████╗  ██║██╔════╝██║
//   ██╔████╔██║███████║███████╗   ██║   █████╗  ██████╔╝    ██████╔╝███████║██╔██╗ ██║█████╗  ██║
//   ██║╚██╔╝██║██╔══██║╚════██║   ██║   ██╔══╝  ██╔══██╗    ██╔═══╝ ██╔══██║██║╚██╗██║██╔══╝  ██║
//   ██║ ╚═╝ ██║██║  ██║███████║   ██║   ███████╗██║  ██║    ██║     ██║  ██║██║ ╚████║███████╗███████╗
//   ╚═╝     ╚═╝╚═╝  ╚═╝╚══════╝   ╚═╝   ╚══════╝╚═╝  ╚═╝    ╚═╝     ╚═╝  ╚═╝╚═╝  ╚═══╝╚══════╝╚══════╝
//
//   MASTER PANEL — Tüm görsel ayarlar buradan yönetilir.
//   Her satırın yanındaki yorum ne işe yaradığını açıklar.
//
// ====================================================================





const PHILOSOPHY = {

  // ─────────────────────────────────────────────
  //  🖼️  FOTOĞRAF (Sağ taraf)
  // ─────────────────────────────────────────────
  photo: {
    src: "/media/images/dhual.jpg", // Fotoğraf dosya yolu

    opacity: 0.85,     // Fotoğraf opaklığı: 0.0 (görünmez) → 1.0 (tam)

    // Fotoğraf alanının genişliği (ekran genişliğinin yüzdesi)
    // Büyütürsen daha fazla fotoğraf, küçültürsen daha az
    width: "w-[55%] md:w-[48%] lg:w-[44%]",

    // Sol → sağ gradient geçişi
    // Küçük % = kısa geçiş (yazılara yakın siyah biter, fotoğraf erken belirir)
    // Büyük % = uzun geçiş (daha fazla alan siyahla örtülür)
    gradientEdge: "60%",

    // Alt ve üst kenarlardaki siyaha dönme mesafesi
    bottomFade: "25%",    // Alttan ne kadar mesafede siyaha döner
    topFade: "15%",    // Üstten ne kadar mesafede siyaha döner
  },

  // ─────────────────────────────────────────────
  //  🎨  RENKLER & TEMA
  // ─────────────────────────────────────────────
  theme: {
    bg: "#050505",       // Arkaplan rengi

    labelColor: "text-black/50 dark:text-white/30", // "our philosophy" etiketi
    line1Color: "text-black dark:text-white",    // "WE ARE YOUR" rengi
    line3Color: "text-black dark:text-white",    // "MENTORS," rengi
    line4Color: "text-black/50 dark:text-white/30", // "AND BUSINESS PARTNERS." — soluk
    bodyTextColor: "text-black/90 dark:text-white/70", // Paragraf metni
  },

  // ─────────────────────────────────────────────
  //  📝  METİNLER (Varsayılanlar)
  // ─────────────────────────────────────────────
  subtitle: "our philosophy",
  paragraphs: {
    p1: "The KNOXOPIA team has created over a hundred projects for companies in various fields. We have found the perfect balance between creative freedom and strict business objectives.",
    p2: "Now we want to help designers and digital creators grow through real projects — it is important to us that your skills benefit you, your company, and your industry.",
  },

  // ─────────────────────────────────────────────
  //  🔤  TİPOGRAFİ
  // ─────────────────────────────────────────────
  //  Tailwind boyut örnekleri:
  //    text-3xl = 30px | text-5xl = 48px | text-7xl = 72px | text-8xl = 96px
  //  Responsive: "text-3xl md:text-5xl lg:text-7xl"
  //
  typography: {
    headingSize: "text-3xl md:text-5xl lg:text-7xl", // Tüm başlık satırları

    line1: { text: "WE ARE YOUR" },
    line2: { text: "CONSULTANTS," }, // ← NEON efekti bu satıra uygulanır
    line3: { text: "MENTORS," },
    line4: { text: "AND BUSINESS PARTNERS." },
    title: { text: "Creative Vision" },
  },

  // ─────────────────────────────────────────────
  //  🌈  NEON EFEKTİ (CONSULTANTS yazısı)
  // ─────────────────────────────────────────────
  neon: {
    // RENK PALETİ — ilk ve son renk aynı = dikişsiz sonsuz döngü
    colors: ["#00f0ff", "#bc13fe", "#ff003c", "#00f0ff"],

    // ÇİZGİ KALINLIĞI: 1 = ince tel | 2 = standart | 4 = kalın bant
    strokeWidth: 2,

    // PARLAMA genişliği: "8px" = dar odaklı | "30px" = geniş atmosfer
    glowSize: "18px",

    // AKIŞ HIZI (saniye) — düşük = hızlı | yüksek = yavaş
    // Fark büyüdükçe segmentler daha farklı ritimde akar = daha organik
    speedMin: 10,
    speedMax: 22,

    // SEGMENT SAYISI — üst üste binen bağımsız neon çizgisi sayısı
    // 2-3 = sade minimal | 4-5 = dengeli | 6-8 = yoğun kaotik
    segmentCount: 5,

    // DASH UZUNLUĞU — küçük = kısa parlak nokta | büyük = uzun akan bant
    dashArrayMin: 200,
    dashArrayMax: 800,

    // FAZ ARALIĞI — büyük = her segment çok farklı noktadan başlar (daha random)
    phaseMin: 0,
    phaseMax: 5000,

    // RENK JITTER: 0 = düzgün gradient | 0.2 = renkler yığılır/açılır
    colorJitter: 0.18,

    // SVG BOYUTLARI — "CONSULTANTS," ~12 harf için ayarlandı
    // vbW büyürse harfler daha seyrek | küçürse daha sıkışık görünür
    // Farklı font kullanıyorsan vbW'yi ±100 oynayarak ayarla
    vbW: 1380,  // viewBox genişliği (harf sayısına göre)
    vbH: 185,   // viewBox yüksekliği
    vbFont: 175,   // font-size (viewBox birimi)
    vbBase: 172,   // baseline y pozisyonu
  },

  // ─────────────────────────────────────────────
  //  🎬  STREAMING PLATFORMLARI (logo PNG alanı)
  // ─────────────────────────────────────────────
  streaming: {
    // Her platformun logosu için görsel yolu
    // Kendi PNG dosyanızı /media/images/ klasörüne koyun ve yolu buraya yazın
    // null bırakırsanız placeholder görüntülenir
    netflix: null, // örn: "/media/images/netflix-logo.png"
    disneyPlus: null, // örn: "/media/images/disney-plus-logo.png"
    amazonPrime: null, // örn: "/media/images/amazon-prime-logo.png"

    // IMDB linki — tıklanınca IMDB'ye gider
    imdbUrl: "https://www.imdb.com", // Kendi IMDB sayfanızın URL'si
  },

  // ─────────────────────────────────────────────
  //  ⚡  GLITCH LİNK EFEKTİ (contact us butonu)
  // ─────────────────────────────────────────────
  glitch: {
    // Karışırken kullanılan karakterler
    chars: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789%&@#$*",

    // Neon renk paleti — nav ile aynı
    colors: ["#ff003c", "#00f0ff", "#bc13fe", "#e6e6e6", "#ffed00"],

    // Harf değişim hızı (ms) — küçük = hızlı glitch
    speed: 60,

    // Her adımda kaç harf orijinaline döner (0.1 = yavaş | 0.5 = hızlı çözülme)
    resolveSpeed: 0.25,

    // Yazı parlaması: her harfin etrafındaki glow
    // (color) => `0 0 8px ${color}` formatı
    textGlow: (color) => `0 0 8px ${color}`,

    // Alt çizgi parlaması — 3 katmanlı bloom efekti
    lineGlow: (color) => `0 0 10px ${color}, 0 0 25px ${color}, 0 0 45px ${color}`,

    // Font ayarları
    fontSize: "12px",
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: "0.3em",

    // Alt çizgi boyutları
    lineHeight: "2px",
    lineOffset: "6px",
  },

  // ─────────────────────────────────────────────
  //  🎬  ANİMASYON
  // ─────────────────────────────────────────────
  animation: {
    // Satır giriş süresi (saniye): 0.6 = hızlı | 1.2 = yavaş sinematik
    revealDuration: 0.8,

    // Her satırın giriş gecikmesi (stagger)
    line1Delay: 0.0,
    line2Delay: 0.1,
    line3Delay: 0.2,
    line4Delay: 0.3,

    // Paragraflar ve linkler için gecikme
    descDelay: 0.5,
    linksDelay: 0.7,

    // Scroll parallax — fotoğrafın dikey hareketi
    // parallaxFrom > 0 = aşağıdan yukarı doğru kayar
    parallaxFrom: 100,
    parallaxTo: -100,
  },

};

// ====================================================================
//   BURADAN AŞAĞI KOD — DEĞİŞTİRMEN GEREKMİYOR
// ====================================================================

// Navigation'daki scramble motorunun aynısı — harf karıştırma + çizgi renk senkronizasyonu
const useScrambleText = (originalText) => {
  const [charStates, setCharStates] = useState(
    originalText.split('').map((char) => ({ char, color: '' }))
  );
  const [lineColor, setLineColor] = useState('');
  const intervalRef = useRef(null);

  const scramble = useCallback(() => {
    let iteration = 0;
    clearInterval(intervalRef.current);
    const { chars, colors, speed, resolveSpeed } = PHILOSOPHY.glitch;
    intervalRef.current = setInterval(() => {
      setLineColor(colors[Math.floor(Math.random() * colors.length)]);
      setCharStates(
        originalText.split('').map((orig, idx) => {
          if (idx < iteration) return { char: orig, color: '' };
          return {
            char: chars[Math.floor(Math.random() * chars.length)],
            color: colors[Math.floor(Math.random() * colors.length)],
          };
        })
      );
      if (iteration >= originalText.length) {
        clearInterval(intervalRef.current);
        setLineColor('');
      }
      iteration += resolveSpeed;
    }, speed);
  }, [originalText]);

  const reset = useCallback(() => {
    clearInterval(intervalRef.current);
    setCharStates(originalText.split('').map((char) => ({ char, color: '' })));
    setLineColor('');
  }, [originalText]);

  return { charStates, lineColor, scramble, reset };
};

// Glitch + neon alt çizgi link bileşeni
const GlitchLink = ({ label, href, onClick, arrow = false }) => {
  const { charStates, lineColor, scramble, reset } = useScrambleText(label.toUpperCase());
  const [hovered, setHovered] = useState(false);
  const { textGlow, lineGlow, fontSize, fontFamily, letterSpacing, lineHeight, lineOffset, colors } = PHILOSOPHY.glitch;
  const activeColor = lineColor || '#ffffff';

  return (
    <a
      href={href}
      className="relative inline-flex items-center gap-3 cursor-pointer py-1"
      style={{ fontSize, fontFamily, letterSpacing, textTransform: 'uppercase' }}
      onMouseEnter={() => { scramble(); setHovered(true); }}
      onMouseLeave={() => { reset(); setHovered(false); }}
      onClick={(e) => { e.preventDefault(); onClick?.(); }}
    >
      {/* Glitch harfler */}
      <div className="flex">
        {charStates.map((obj, i) => (
          <span key={i} style={{
            color: obj.color || '#ffffff',
            opacity: obj.color ? 1 : 0.5,
            textShadow: obj.color ? textGlow(obj.color) : 'none',
            transition: obj.color ? 'none' : 'all 0.4s ease-out',
          }}>
            {obj.char}
          </span>
        ))}
      </div>

      {/* Ok ikonu */}
      {arrow && (
        <motion.svg
          className="w-4 h-4"
          style={{ color: hovered ? activeColor : 'rgba(255,255,255,0.5)' }}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
          animate={{ x: hovered ? 4 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </motion.svg>
      )}

      {/* Neon alt çizgi — nav ile aynı 3 katmanlı glow */}
      <motion.div
        className="absolute left-0 w-full origin-left"
        style={{ height: lineHeight, bottom: `-${lineOffset}` }}
        animate={{
          scaleX: hovered ? 1 : 0,
          backgroundColor: activeColor,
          boxShadow: hovered ? lineGlow(activeColor) : 'none',
          opacity: hovered ? [1, 0.8, 1, 0.9, 1] : 0,
        }}
        transition={{
          scaleX: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
          backgroundColor: { duration: 0.1 },
          opacity: { repeat: Infinity, duration: 1.5, ease: 'linear' },
        }}
      />
    </a>
  );
};

const rand = (min, max) => Math.random() * (max - min) + min;

const useNeonSegments = (overrideColors = null, overrideSpeedMin = null, overrideSpeedMax = null) => {
  const colorsKey = overrideColors ? overrideColors.join(',') : 'default';
  const speedKey = `${overrideSpeedMin ?? 'def'}_${overrideSpeedMax ?? 'def'}`;

  return useMemo(() => {
    const {
      segmentCount,
      dashArrayMin, dashArrayMax,
      phaseMin, phaseMax,
      colorJitter,
    } = PHILOSOPHY.neon;
    const speedMin = overrideSpeedMin ?? PHILOSOPHY.neon.speedMin;
    const speedMax = overrideSpeedMax ?? PHILOSOPHY.neon.speedMax;
    const colors = overrideColors || PHILOSOPHY.neon.colors;

    return Array.from({ length: segmentCount }, (_, idx) => {
      const speed = rand(speedMin, speedMax);
      const dashArr = rand(dashArrayMin, dashArrayMax);
      const phase = rand(phaseMin, phaseMax);
      const gradId = `pg_${idx}_${idx}${segmentCount}`;

      const shift = Math.floor(Math.random() * colors.length);
      const shifted = [...colors.slice(shift), ...colors.slice(0, shift)];
      const last = shifted.length - 1;
      const stops = shifted.map((color, i) => {
        const base = i / last;
        const jitter = (i === 0 || i === last) ? 0 : rand(-colorJitter, colorJitter);
        return { color, offset: `${(Math.min(1, Math.max(0, base + jitter)) * 100).toFixed(1)}%` };
      });

      const glowColor = shifted[0];
      const segOpacity = rand(0.55, 1.0);
      // currentOffset: başlangıç faz offseti (JS animasyonu için)
      const startOffset = phase;

      return { speed, dashArr, phase, startOffset, gradId, stops, glowColor, segOpacity };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorsKey, speedKey]);
};


// Hollow SVG neon — iOS Safari uyumlu rAF tabanlı animasyon
// CSS @keyframes + SVG stroke-dashoffset kombinasyonu WebKit'te güvenilir çalışmaz.
// Bunun yerine requestAnimationFrame ile doğrudan DOM manipülasyonu yapılır.
const NeonHollowText = ({ text, apiNeon = null, fontSize = null }) => {
  const strokeWidth = apiNeon?.philosophy_neon_stroke ?? apiNeon?.neon_stroke_width ?? PHILOSOPHY.neon.strokeWidth;
  const glowSize = apiNeon?.philosophy_neon_glow ?? apiNeon?.neon_glow_size ?? PHILOSOPHY.neon.glowSize;
  const { vbW, vbH, vbFont, vbBase } = PHILOSOPHY.neon;
  const apiColors = apiNeon?.philosophy_neon_colors ?? apiNeon?.neon_colors ?? null;
  const speedMin = apiNeon?.philosophy_neon_speed_min ?? PHILOSOPHY.neon.speedMin;
  const speedMax = apiNeon?.philosophy_neon_speed_max ?? PHILOSOPHY.neon.speedMax;
  const segments = useNeonSegments(apiColors, speedMin, speedMax);

  const uid = useRef(`ph_neon`).current;
  const glowPx = parseFloat(glowSize);
  const svgRef = useRef(null);
  const rafRef = useRef(null);

  // rAF animasyon döngüsü — her tarayıcıda (Safari dahil) çalışır
  useEffect(() => {
    // Her segment kendi ofset durumunu tutar
    const state = segments.map(seg => ({
      offset: seg.startOffset,
      velocity: seg.dashArr / (seg.speed * 1000),
      dashArr: seg.dashArr,
    }));

    let lastTime = null;

    const tick = (now) => {
      if (lastTime !== null) {
        const delta = now - lastTime;
        // Her segment artık ayrı SVG içinde — data-seg-idx ile bulunur
        const textEls = document.querySelectorAll(`[data-svg-group="${uid}"] [data-seg-idx]`);
        if (textEls && textEls.length) {
          textEls.forEach((el) => {
            const i = parseInt(el.getAttribute('data-seg-idx'), 10);
            if (state[i] === undefined) return;
            state[i].offset -= state[i].velocity * delta;
            if (state[i].offset < 0) {
              state[i].offset += state[i].dashArr * 100;
            }
            el.setAttribute('stroke-dashoffset', state[i].offset.toFixed(2));
          });
        }
      }
      lastTime = now;
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [segments, uid]);

  return (
    <div className="relative w-full" style={{ lineHeight: 0, fontSize: fontSize || undefined }}>
      <div style={{ position: 'relative', width: '100%', paddingBottom: `${(vbH / vbW * 100).toFixed(2)}%` }}>
        {/* Her segment ayrı div içinde — iOS'ta SVG text'e filter uygulanamaz,
            sarmalayan div'e uygulamak tüm tarayıcılarda çalışır */}
        {segments.map(({ gradId, dashArr, startOffset, glowColor, segOpacity, stops }, i) => (
          <div
            key={gradId}
            style={{
              position: 'absolute', inset: 0,
              opacity: segOpacity,
              filter: `drop-shadow(0 0 ${glowPx}px ${glowColor})`,
            }}
          >
            <svg
              ref={i === 0 ? svgRef : undefined}
              data-svg-group={uid}
              viewBox={`0 0 ${vbW} ${vbH}`}
              preserveAspectRatio="xMinYMid meet"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}
            >
              <defs>
                <linearGradient id={`${uid}_${gradId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  {stops && stops.map((s, si) => <stop key={si} offset={s.offset} stopColor={s.color} />)}
                </linearGradient>
              </defs>
              <text
                data-seg-idx={i}
                x="0" y={vbBase}
                fontSize={vbFont} fontWeight="bold"
                fill="none"
                stroke={`url(#${uid}_${gradId})`}
                strokeWidth={strokeWidth}
                strokeDasharray={dashArr}
                strokeDashoffset={startOffset}
                textAnchor="start"
                className="uppercase"
                style={{ fontFamily: 'inherit' }}
              >
                {text}
              </text>
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
};


// Alttan yukarı text reveal
const TextReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: '100%' }}
        animate={isInView ? { y: 0 } : { y: '100%' }}
        transition={{ duration: PHILOSOPHY.animation.revealDuration, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};


// IMDB SVG logosu (resmi altın sarısı renkli)
const ImdbIcon = () => (
  <svg viewBox="0 0 72 28" fill="none" className="w-10 h-4" xmlns="http://www.w3.org/2000/svg">
    <rect width="72" height="28" rx="4" fill="#F5C518" />
    <text x="6" y="21" fontFamily="Arial Black, Arial" fontWeight="900" fontSize="17" fill="#000000" letterSpacing="-0.5">IMDb</text>
  </svg>
);

// Streaming Platformları + IMDB bileşeni
const StreamingPlatforms = ({ apiSettings }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [imdbHovered, setImdbHovered] = useState(false);

  const { streaming } = PHILOSOPHY;
  const imdbUrl = apiSettings?.philosophy_imdb_url || streaming.imdbUrl;
  const netflixSrc = apiSettings?.philosophy_netflix_logo || streaming.netflix;
  const disneyPlusSrc = apiSettings?.philosophy_disneyplus_logo || streaming.disneyPlus;
  const amazonPrimeSrc = apiSettings?.philosophy_amazonprime_logo || streaming.amazonPrime;

  const platforms = [
    { key: 'netflix', src: netflixSrc, label: 'Netflix', placeholderText: 'NETFLIX' },
    { key: 'disneyPlus', src: disneyPlusSrc, label: 'Disney+', placeholderText: 'DISNEY+' },
    { key: 'amazonPrime', src: amazonPrimeSrc, label: 'Amazon Prime', placeholderText: 'PRIME' },
  ];

  const imdbColor = '#F5C518';

  return (
    <motion.div
      ref={ref}
      className="mt-16 md:mt-24 max-w-4xl"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7, delay: 0.65 }}
    >
      {/* Başlık çizgisi */}
      <div className="flex items-center gap-4 mb-8">
        <div className="h-px bg-black/10 dark:bg-white/10" style={{ width: '2rem' }} />
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-black/40 dark:text-white/25">{t('philosophy', 'as_seen_on', 'as seen on')}</span>
        <div className="h-px flex-1 bg-black/10 dark:bg-white/10" />
      </div>

      {/* Platform logoları + IMDB */}
      <div className="flex flex-wrap items-center gap-4 md:gap-6">

        {/* Netflix / Disney+ / Amazon Prime PNG slotları */}
        {platforms.map(({ key, src, label, placeholderText }) => (
          <div
            key={key}
            className="relative flex items-center justify-center overflow-hidden"
            style={{
              width: '120px',
              height: '48px',
              border: '1px solid rgba(255,255,255,0.08)',
              backgroundColor: 'rgba(255,255,255,0.03)',
            }}
          >
            {src ? (
              <img
                src={src}
                alt={label}
                className="object-contain w-full h-full"
                style={{ padding: '8px', filter: 'brightness(0.9) contrast(1.1)' }}
              />
            ) : (
              /* Placeholder — kendi logoğunuzu koyduğunuzda kaybolur */
              <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/15">{placeholderText}</span>
                <span className="font-mono text-[6px] uppercase tracking-[0.15em] text-black/30 dark:text-white/10">{t('philosophy', 'logo_here', 'logo buraya')}</span>
              </div>
            )}
          </div>
        ))}

        {/* Ayırıcı */}
        <div className="w-px h-8 bg-black/10 dark:bg-white/10" />

        {/* IMDB — Tıklanabilir link */}
        <motion.a
          href={imdbUrl}
          target="_blank"
          rel="noreferrer"
          className="relative flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
          style={{
            border: `1px solid ${imdbHovered ? imdbColor + '80' : 'rgba(255,255,255,0.08)'}`,
            backgroundColor: imdbHovered ? `${imdbColor}0d` : 'transparent',
            boxShadow: imdbHovered
              ? `0 0 12px ${imdbColor}55, 0 0 30px ${imdbColor}22, inset 0 0 12px ${imdbColor}0a`
              : 'none',
            transition: 'border-color 0.3s, background-color 0.3s, box-shadow 0.3s',
          }}
          onHoverStart={() => setImdbHovered(true)}
          onHoverEnd={() => setImdbHovered(false)}
        >
          {/* Arka glow */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{ opacity: imdbHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            style={{ background: `radial-gradient(ellipse at left center, ${imdbColor}18 0%, transparent 70%)` }}
          />

          {/* IMDB Logosu */}
          <motion.span
            className="relative z-10 flex-shrink-0"
            animate={{
              filter: imdbHovered
                ? `drop-shadow(0 0 6px ${imdbColor}) drop-shadow(0 0 12px ${imdbColor}88)`
                : 'none',
            }}
            transition={{ duration: 0.25 }}
          >
            <ImdbIcon />
          </motion.span>

          {/* Label */}
          <motion.span
            className="relative z-10 font-mono text-xs uppercase tracking-[0.2em]"
            animate={{ color: imdbHovered ? imdbColor : 'rgba(255,255,255,0.35)' }}
            transition={{ duration: 0.25 }}
            style={{ textShadow: imdbHovered ? `0 0 10px ${imdbColor}88` : 'none' }}
          >
            IMDb
          </motion.span>

        </motion.a>

        {/* Buton Dışındaki Dinamik Ok (Sola Bakan) */}
        <motion.div
          className="hidden sm:flex items-center"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: [0, -8, 0] }}
          transition={{
            opacity: { duration: 0.5, delay: 0.8 },
            x: { duration: 2, repeat: Infinity, ease: "easeInOut" }
          }}
        >
          <svg
            className="w-6 h-6 text-[#F5C518]/60"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: `drop-shadow(0 0 5px ${imdbColor}33)` }}
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#F5C518]/40 ml-2"></span>
        </motion.div>
      </div>
    </motion.div>
  );
};


export const PhilosophySection = () => {
  const apiSettings = useSettings();
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [
    PHILOSOPHY.animation.parallaxFrom,
    PHILOSOPHY.animation.parallaxTo,
  ]);

  const { photo, theme, animation } = PHILOSOPHY;

  // Yazılar
  const l1Text = t('philosophy', 'line1', PHILOSOPHY.typography.line1.text);
  const l2Text = t('philosophy', 'line2', PHILOSOPHY.typography.line2.text);
  const l3Text = t('philosophy', 'line3', PHILOSOPHY.typography.line3.text);
  const l4Text = t('philosophy', 'line4', PHILOSOPHY.typography.line4.text);
  const titleText = t('philosophy', 'title', PHILOSOPHY.typography.title.text);

  // Görünürlük
  const l1Vis = apiSettings?.philosophy_line1_visible !== false;
  const l2Vis = apiSettings?.philosophy_line2_visible !== false;
  const l3Vis = apiSettings?.philosophy_line3_visible !== false;
  const l4Vis = apiSettings?.philosophy_line4_visible !== false;

  // Font boyutları → clamp()
  const makeSize = (mobKey, deskKey, mobDef, deskDef) => {
    const mob = apiSettings?.[mobKey] ?? mobDef;
    const desk = apiSettings?.[deskKey] ?? deskDef;
    return `clamp(${(desk * 0.55).toFixed(2)}rem, ${mob}vw, ${desk}rem)`;
  };
  const l1Size = makeSize('philosophy_line1_size_mobile', 'philosophy_line1_size_desktop', 7.5, 4.5);
  const l2Size = makeSize('philosophy_line2_size_mobile', 'philosophy_line2_size_desktop', 7.5, 4.5);
  const l3Size = makeSize('philosophy_line3_size_mobile', 'philosophy_line3_size_desktop', 7.5, 4.5);
  const l4Size = makeSize('philosophy_line4_size_mobile', 'philosophy_line4_size_desktop', 7.5, 4.5);

  // Paragraflar & etiket
  const para1 = t('philosophy', 'para1', PHILOSOPHY.paragraphs.p1);
  const para2 = t('philosophy', 'para2', PHILOSOPHY.paragraphs.p2);
  const subtitle = t('philosophy', 'subtitle', PHILOSOPHY.subtitle);

  const photoSrc = apiSettings?.philosophy_photo || photo.src;

  return (
    <section
      id="philosophy"
      ref={ref}
      data-testid="philosophy-section"
      className="relative min-h-screen py-32 md:py-48 px-6 md:px-12 lg:px-24 overflow-hidden"
      style={{ background: theme.bg, willChange: 'auto' }}
    >

      {/* ── Sağdaki dikey fotoğraf ── */}
      <motion.div
        className={`absolute inset-y-0 right-0 ${photo.width} pointer-events-none`}
        style={{ y }}
      >
        <img
          src={photoSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: photo.opacity }}
          loading="lazy"
        />
        {/* Sol → sağ gradient */}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(to right, ${theme.bg} 0%, ${theme.bg} 8%, rgba(5,5,5,0.82) 28%, rgba(5,5,5,0.35) ${photo.gradientEdge}, rgba(5,5,5,0.0) 100%)`,
        }} />
        {/* Alt gradient */}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(to top, ${theme.bg} 0%, transparent ${photo.bottomFade})`,
        }} />
        {/* Üst gradient */}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(to bottom, ${theme.bg} 0%, transparent ${photo.topFade})`,
        }} />
      </motion.div>

      <div className="max-w-screen-2xl mx-auto relative">

        {/* Etiket */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        >
          <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.3em] ${theme.labelColor} border border-black/20 dark:border-white/10 px-4 py-2`}>
            {subtitle}
          </span>
        </motion.div>

        {/* Ana başlık — 4 satır */}
        <div className="max-w-5xl space-y-2 md:space-y-3">

          {l1Vis && (
            <TextReveal delay={animation.line1Delay}>
              <h2 className={`font-display font-bold leading-[1.1] ${theme.line1Color}`} style={{ fontSize: l1Size }}>
                {l1Text}
              </h2>
            </TextReveal>
          )}

          {/* NEON satırı */}
          {l2Vis && (
            <TextReveal delay={animation.line2Delay}>
              <NeonHollowText text={l2Text} apiNeon={apiSettings} fontSize={l2Size} />
            </TextReveal>
          )}

          {l3Vis && (
            <TextReveal delay={animation.line3Delay}>
              <h2 className={`font-display font-bold leading-[1.1] ${theme.line3Color}`} style={{ fontSize: l3Size }}>
                {l3Text}
              </h2>
            </TextReveal>
          )}

          {l4Vis && (
            <TextReveal delay={animation.line4Delay}>
              <h2 className={`font-display font-bold leading-[1.1] ${theme.line3Color}`} style={{ fontSize: l4Size }}>
                {l4Text}
              </h2>
            </TextReveal>
          )}

        </div>

        {/* Paragraflar */}
        <motion.div
          className="mt-20 md:mt-32 grid md:grid-cols-2 gap-10 md:gap-20 max-w-4xl"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, delay: animation.descDelay }}
        >
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-white/40" />
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-black/60 dark:text-white/40">about us</span>
            </div>
            <p className="text-base md:text-lg leading-[1.9] font-body"
              style={{ color: 'rgba(255,255,255,0.82)', letterSpacing: '0.01em' }}>
              {para1}
            </p>
          </div>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-white/40" />
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-black/60 dark:text-white/40">our vision</span>
            </div>
            <p className="text-base md:text-lg leading-[1.9] font-body"
              style={{ color: 'rgba(255,255,255,0.82)', letterSpacing: '0.01em' }}>
              {para2}
            </p>
          </div>
        </motion.div>

        {/* ─── Streaming Platformları + IMDB ─────────────────── */}
        <StreamingPlatforms apiSettings={apiSettings} />

        {/* İletişim linkleri */}
        <motion.div
          className="mt-16 flex flex-wrap items-center gap-8"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: animation.linksDelay }}
        >
          {/* Glitch + neon alt çizgi efektli contact butonu */}
          <GlitchLink
            label="contact"
            href="#contact"
            arrow={true}
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          />

          <span className="w-px h-4 bg-black/10 dark:bg-white/10" />

          <a
            href="mailto:knoxopia@gmail.com"
            className="font-mono text-xs tracking-[0.1em] text-black/50 dark:text-white/30 hover:text-black/80 dark:text-white/60 transition-colors"
          >
            knoxopia@gmail.com
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default PhilosophySection;